import React from "react";
import { Button } from "../../misc/Button";
import { cn } from "../../../utils/classMerger";
import type { AddressCandidate } from "../../../types/addressCandidate";
import {
    candidateAgentLine,
    candidateFactsLine,
    candidateHeading,
    candidatePriceLabel,
} from "../../../utils/addressCandidateCopy";

interface AddressCandidatePickerProps {
    address: string;
    candidates: AddressCandidate[];
    onPick: (mlsId: string) => void;
    onBack: () => void;
    className?: string;
}

const AddressCandidatePicker: React.FC<AddressCandidatePickerProps> = ({
    address,
    candidates,
    onPick,
    onBack,
    className,
}) => (
    <section
        className={cn("w-full", className)}
        aria-labelledby="address-candidate-heading"
    >
        <h2
            id="address-candidate-heading"
            className="text-xl font-bold text-primary"
        >
            Which listing did you mean?
        </h2>
        <p className="mt-2 text-gray-700">
            {candidateHeading(candidates.length, address)}
        </p>

        <ul className="mt-6 flex flex-col gap-3">
            {candidates.map((candidate) => (
                <li
                    key={candidate.mlsId}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-secondary hover:bg-gray-50 min-[640px]:flex-row min-[640px]:items-center"
                >
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-2 text-primary">
                            <span className="font-semibold">
                                {candidate.typeLabel}
                            </span>
                            <span className="font-semibold">
                                {candidatePriceLabel(candidate)}
                            </span>
                            {candidate.alreadyInAccount && (
                                <span className="rounded-full bg-[#e9c468] px-2 py-0.5 text-[11px] font-medium text-[#193456]">
                                    Already in your listings
                                </span>
                            )}
                        </div>
                        {candidateFactsLine(candidate) && (
                            <p className="text-sm text-gray-700">
                                {candidateFactsLine(candidate)}
                            </p>
                        )}
                        {candidate.remarksSnippet && (
                            <p className="text-sm text-gray-600">
                                {candidate.remarksSnippet}
                            </p>
                        )}
                        <p className="text-xs text-gray-500">
                            {[candidateAgentLine(candidate), `MLS# ${candidate.mlsId}`]
                                .filter(Boolean)
                                .join(" · ")}
                        </p>
                    </div>
                    <Button
                        type="button"
                        onClick={() => onPick(candidate.mlsId)}
                        disabled={!candidate.mlsId || candidate.alreadyInAccount}
                        className="w-full min-[640px]:w-auto"
                        aria-label={`Choose MLS ${candidate.mlsId}`}
                    >
                        Choose
                    </Button>
                </li>
            ))}
        </ul>

        <div className="mt-6 flex flex-col gap-2 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
            <Button type="button" variant="outline" onClick={onBack}>
                Back
            </Button>
            <p className="text-xs text-gray-500">
                Not seeing the one you want? Go back and add it by MLS number.
            </p>
        </div>
    </section>
);

export default AddressCandidatePicker;
